"use client";

import { useEffect, useMemo, useState } from "react";
import { useHistoryManager } from "../history/useHistoryManager";
import { useQuery } from "@tanstack/react-query";
import { useClient, useToken } from "../../lib/context/auth-context";
import StoreBox from "../store/store-box";
import styles from "./index.module.css";
import Carousel from "../carousel/carousel";
import EmptyState from "../common/EmptyState";
import {
  buildQueryString,
  extractInfoFromUrl,
  formatCurrency,
  generateAbbreviation,
} from "../../lib/util";
import { useProductPriceDisplay } from "../product/useProductPriceDisplay";
import Skeleton from "react-loading-skeleton";
import Pagination from "../common/Pagination";
import { useAppDispatch } from "../slice/hooks";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import { useSwitchingOrgs } from "../organisation/useSwitchingOrgs";
import { rightArrowIcon } from "../../assets";
import MiniCarousel from "../carousel/miniCarousel";

const MarketPlace = (props) => {
  const { storePath, searchTerm, setSearchTerm, urlData } = props;
  const [error, setError] = useState(null);
  const [hasTyped, setHasTyped] = useState(false);
  const [query, setQuery] = useState("");

  const dispatch = useAppDispatch();

  const { token } = useToken();
  const client = useClient();
  const { navigateTo } = useHistoryManager(storePath);

  const [state, setState] = useState({
    products: [],
    limit: 20,
    total: 0,
    currentPage: 1,
    page: 1,
    search: "",
  });

  const queryKey = useMemo(
    () => ["feeds", state.limit, state.page, query],
    [state.limit, state.page, query],
  );
  const {
    data: feeds,
    isLoading,
    isFetching,
    refetch,
    isRefetching,
  } = useQuery(
    queryKey,
    async () => {
      const queryParams = [];

      if (query) {
        queryParams.push(`searchTerm=${query}`);
      }
      queryParams.push(`limit=${state.limit}`);
      queryParams.push(`page=${state.page}`);
      const params = queryParams.length ? `?${queryParams.join("&")}` : "";
      const res = await client(`product/products${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      onError: (err) => {
        setError(err.message);
        console.error("Failed to fetch products:", err);
      },
    },
  );

  const { data: _categories = [], isLoading: isCategoryLoading } = useQuery(
    ["_categories", token],
    async () => {
      const res = await client(
        `configuration/public/category/product/categories`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    },
  );

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 750);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    dispatch(setAccessPointId(null));
    dispatch(setAccessPoint(null));
    dispatch(setAccessPoints([]));
    dispatch(setOrganisation(null));
    dispatch(setActiveCategory(null));
    dispatch(setCategories(null));
  }, []);

  useEffect(() => {
    const q = extractInfoFromUrl("q");
    if (!q) return;
    setSearchTerm(q);
  }, []);

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      setHasTyped(true);
    }

    const delayDebounce = setTimeout(() => {
      activateSearch();
    }, 1000); // wait 300ms after typing stops

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const resetPage = () => {
    // if (resetCategory) dispatch(setActiveCategory(""));
    // if (persistQuery) storeInState();
    setState((prev) => ({
      ...prev,
      products: [],
      total: 0,
      currentPage: 1,
      page: 1,
    }));
  };

  const activateSearch = (value = null) => {
    const rawValue = value !== null ? value : searchTerm || "";
    const trimmed = rawValue.trim();
    if (query.trim() == trimmed) return;

    // Reset pagination state before searching
    resetPage(false, false);

    // Store the active query
    setQuery(trimmed);
    const urlQuery = buildQueryString({ q: trimmed });
    navigateTo(`${window.location.pathname}${urlQuery}`, { replace: true });
  };

  const ProductPrice = ({
    product,
    accessPoint = null,
    recievedPriceOption = null,
  }) => {
    const { productPrice, getDisplayPrice } = useProductPriceDisplay(
      accessPoint,
      product,
      recievedPriceOption,
    );
    const amount =
      productPrice ?? getDisplayPrice(product) ?? product?.amount ?? 0;
    let formatted;
    try {
      formatted = formatCurrency(amount, product.currency);
    } catch (e) {
      formatted = `${amount}`;
    }
    return (
      <h4
        className={styles.priceLine}
        style={{ fontSize: "1.9rem", fontWeight: 500 }}
      >
        {formatted}
      </h4>
    );
  };

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, currentPage } = feeds;
      setState((prev) => {
        const all = [...(prev.products || []), ...products];
        const unique = Array.from(
          new Map(all.map((p) => [p.productId, p])).values(),
        );
        return { ...prev, products: unique, limit, total, currentPage };
      });
    }
  }, [feeds]);

  const openPage = (page) => {
    setState((prev) => ({ ...prev, page }));
  };

  const {
    startSwitch,
    isSwitching: hookSwitching,
    error: switchError,
  } = useSwitchingOrgs({ token });

  //   const hookSwitching = true;

  const handleProductClick = (product) => {
    startSwitch(product);
  };

  //   const hookSwitching = true;

  return (
    <StoreBox
      {...props}
      isSwitching={false}
      hasTyped={hasTyped}
      setHasTyped={setHasTyped}
    >
      <>
        {!hookSwitching && (
          <div className={styles.marketPlaceTop}>
            <div className={styles.marketPlaceHeader}>
              <h1>Welcome to Circleplug</h1>
            </div>
            {!searchTerm && (
              <div className={styles.marketPlaceCategories}>
                {(isCategoryLoading ||
                  _categories?.categories?.length === 0) && (
                  <>
                    <div style={{ flex: 2 }}>
                      <Skeleton
                        height="100%"
                        width="100%"
                        style={{ borderRadius: "7px" }}
                      />
                    </div>

                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} style={{ flex: 1 }}>
                        <Skeleton
                          height="100%"
                          width="100%"
                          style={{ borderRadius: "7px" }}
                        />
                      </div>
                    ))}
                  </>
                )}
                {!isCategoryLoading && _categories?.categories?.length > 0 && (
                  <>
                    <div className={styles.mainCategoriesList} role="button">
                      {_categories.categories.map((mainCat) => (
                        <div
                          key={mainCat.categoryId}
                          className={styles.mainCategoriesItem}
                        >
                          <span>{mainCat.name}</span>{" "}
                          <span>{rightArrowIcon}</span>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        flex: 2,
                        position: "relative",
                        overflow: "hidden", // prevents spill
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <MiniCarousel
                        categories={_categories.categories}
                        setSearchTerm={setSearchTerm}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className={styles.marketPlaceContainer}>
          <div className={styles.productsContainer}>
            {(isLoading || isFetching) && state.products.length === 0 && (
              <div className={styles.productsContainer}>
                {Array.from({ length: 20 }).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "1rem",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                      alignItems: "stretch",
                    }}
                    className={styles.productSkeleton}
                  >
                    <div style={{ width: "100%" }}>
                      <Skeleton
                        height={"21rem"}
                        style={{ borderRadius: 8, display: "block" }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.6rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <Skeleton height={12} width={"40%"} />
                      <Skeleton height={16} width={"60%"} />
                      <Skeleton height={18} width={"80%"} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hookSwitching && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div className={styles.productDetails}>
                  <div
                    style={{
                      display: "flex",
                      height: isMobile ? "80vh" : "40rem",
                      width: "100%",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "space-evenly",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        width: isMobile ? "100%" : "40%",
                        height: isMobile ? "100%" : "85%",
                        flexDirection: "column",
                        gap: "1rem",
                        // marginBottom: isMobile ? "2rem" : 0,
                        alignItems: isMobile ? "center" : "initial",
                        padding: isMobile ? "0 2rem" : 0,
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: isMobile ? "100%" : "80%",
                        }}
                      >
                        <Skeleton width="100%" height="100%" />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            width: isMobile ? "50px" : "60px",
                            height: isMobile ? "50px" : "60px",
                          }}
                        >
                          <Skeleton width="100%" height="100%" />
                        </div>
                        <div
                          style={{
                            width: isMobile ? "50px" : "60px",
                            height: isMobile ? "50px" : "60px",
                          }}
                        >
                          <Skeleton width="100%" height="100%" />
                        </div>
                        <div
                          style={{
                            width: isMobile ? "50px" : "60px",
                            height: isMobile ? "50px" : "60px",
                          }}
                        >
                          <Skeleton width="100%" height="100%" />
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        height: isMobile ? "100%" : "85%",
                        width: isMobile ? "100%" : "40%",
                        flexDirection: "column",
                        gap: "1rem",
                        padding: isMobile ? "4rem 2rem" : "4rem",
                        paddingTop: isMobile ? "4rem" : "1rem",
                      }}
                    >
                      <div style={{ width: "100%", height: "42%" }}>
                        <Skeleton
                          width="100%"
                          height="38%"
                          style={{ marginBottom: isMobile ? "1rem" : "2rem" }}
                        />
                        <Skeleton width="85%" height="38%" />
                      </div>

                      <div
                        style={{
                          width: "100%",
                          // height: "40%",
                          flex: 1,
                          display: "flex",
                          gap: "1rem",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            height: "48%",
                            display: "flex",
                            gap: "1rem",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "50%",
                              height: isMobile ? "100%" : "80%",
                            }}
                          >
                            <Skeleton height="100%" />
                          </div>
                          <div
                            style={{
                              width: "50%",
                              height: isMobile ? "100%" : "80%",
                            }}
                          >
                            <Skeleton height="100%" />
                          </div>
                        </div>
                        <div style={{ height: isMobile ? "30%" : "4rem" }}>
                          <Skeleton width="100%" height="100%" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {state.products.length === 0 &&
              !isLoading &&
              !error &&
              !isFetching && (
                <div className={styles.marketPlaceContainerEmpty}>
                  <EmptyState>
                    There are no available products at this time. Check Back
                    later...
                  </EmptyState>{" "}
                </div>
              )}

            {!hookSwitching &&
              !isLoading &&
              state.products.map((product) => {
                const hasMedia =
                  Array.isArray(product.media) && product.media.length > 0;
                const media = product.media
                  ?.map((m) => m)
                  .filter(Boolean)
                  .map((m) => ({
                    mediaUrl: m.mediaUrl,
                    mediaThumb: m.mediaThumb,
                    mediaName: m.mediaName,
                    mediaType: m.mediaType,
                    mediaExt: m.mediaExt,
                    mediaWidth: m.mediaWidth,
                    mediaHeight: m.mediaHeight,
                    mediaTime: m.mediaTime,
                    _id: m._id,
                  }));

                return (
                  <div
                    key={product.productId}
                    data-product-id={product.productId}
                    onClick={() => handleProductClick(product)}
                    className={styles.productItem}
                    tabIndex={0}
                    role="button"
                  >
                    {hasMedia ? (
                      <div style={{ paddingTop: "1rem" }}>
                        <Carousel
                          media={media}
                          enablePreview={false}
                          showIndicator={false}
                          customHeight={"20rem"}
                          galleryWidth={325}
                          hideThumb={true}
                          autoscroll={true}
                          inMainMarketPlace={true}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "1rem 0",
                          width: "100%",
                        }}
                        className={styles.productItemSkeleton}
                      >
                        <div
                          style={{
                            height: "19.5rem",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f0f0f0",
                            padding: "1rem",
                            boxSizing: "border-box",
                          }}
                          aria-hidden
                        >
                          <div
                            style={{
                              fontSize: "3.5rem",
                              color: "#444",
                              textTransform: "uppercase",
                              fontWeight: 700,
                            }}
                          >
                            {generateAbbreviation(
                              product.name || product.productId,
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className={styles.itemInfo}>
                      <h4>
                        <p className={styles.names}>{product.name}</p>
                      </h4>

                      <ProductPrice
                        product={product}
                        accessPoint={null}
                        recievedPriceOption={product?.storeFrontDisplayPrice}
                      />

                      <h4>
                        <span>Sold by</span>{" "}
                        <p className={styles.names}>{product.host.name}</p>
                      </h4>
                    </div>
                  </div>
                );
              })}
          </div>

          {!hookSwitching && (
            <Pagination
              infiniteScroll
              total={state.total}
              limit={state.limit}
              page={state.page}
              onPageClick={openPage}
              enableManualMode
              autoLoadThreshold={5}
              isLoading={isLoading}
              isFetching={isFetching}
              showEndOfList
              endOfListText="🎉 You've reached the end!"
            />
          )}
        </div>
      </>
    </StoreBox>
  );
};

export default MarketPlace;
